/**
 * SkillPickerPanel — animated full-width panel that expands below a CategoryTileCard.
 *
 * Each QuickSkillRow in the panel has:
 *  - An amber urgency dot when is_urgent = true
 *  - skill_name (13px semibold)
 *  - signal_label (11px slate-500) — the data-grounded signal driving this suggestion
 *  - A teal "Run →" button that fires onLaunch(trigger_prompt, skill_id)
 *
 * The `signal_label` is also passed as a `signal` URL query param so the
 * ChatShellPage header can display it in an amber badge next to the SkillBadge.
 *
 * Animation: CSS max-height transition (no framer-motion) keeps bundle size minimal.
 */

import { ArrowRight, X } from 'lucide-react';
import { clsx } from 'clsx';
import type { CategoryTile, QuickSkillRow as QuickSkillRowType } from '@/services/api';

// ---------------------------------------------------------------------------
// QuickSkillRow
// ---------------------------------------------------------------------------

interface QuickSkillRowProps {
  skill: QuickSkillRowType;
  onLaunch: (prompt: string, skillId: string, signalLabel: string) => void;
}

function QuickSkillRow({ skill, onLaunch }: QuickSkillRowProps) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors duration-100 group/row">
      {/* Urgency dot */}
      <div className="shrink-0 w-4 flex items-center justify-center">
        {skill.is_urgent && (
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Needs attention" />
        )}
      </div>

      {/* Text content */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-800 leading-tight">
          {skill.skill_name}
        </p>
        <p className="text-[11px] text-slate-500 leading-snug mt-0.5 truncate">
          {skill.signal_label}
        </p>
      </div>

      {/* Time hint */}
      <span className="text-[10px] text-slate-300 font-mono tabular-nums shrink-0 hidden sm:block">
        ~{skill.estimated_seconds}s
      </span>

      {/* Run button */}
      <button
        type="button"
        onClick={() => onLaunch(skill.trigger_prompt, skill.skill_id, skill.signal_label)}
        className={clsx(
          'shrink-0 flex items-center gap-1 text-[11px] font-semibold text-brand-teal',
          'border border-brand-teal/30 rounded-lg px-2.5 py-1 transition-all duration-150',
          'hover:bg-brand-teal hover:text-white hover:border-brand-teal',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/40',
        )}
      >
        Run
        <ArrowRight size={10} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkillPickerPanel
// ---------------------------------------------------------------------------

export interface SkillPickerPanelProps {
  tile: CategoryTile;
  isOpen: boolean;
  /** Called with (trigger_prompt, skill_id, signal_label) — parent navigates to chat */
  onLaunch: (prompt: string, skillId: string, signalLabel: string) => void;
  onClose: () => void;
}

export function SkillPickerPanel({ tile, isOpen, onLaunch, onClose }: SkillPickerPanelProps) {
  return (
    <div
      style={{
        maxHeight: isOpen ? '380px' : '0px',
        overflow: 'hidden',
        transition: 'max-height 220ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      aria-hidden={!isOpen}
    >
      <div className="rounded-2xl border border-slate-200 bg-white shadow-md mx-0.5 mt-2 mb-1 overflow-hidden">
        {/* Panel header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">{tile.skill_name}</span>
            {tile.signal_count > 0 && (
              <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                {tile.signal_count} signal{tile.signal_count !== 1 ? 's' : ''} detected
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/40"
            aria-label="Close picker"
          >
            <X size={14} />
          </button>
        </div>

        {/* Sub-skill rows */}
        <div className="py-1">
          {tile.sub_skills.map((skill) => (
            <QuickSkillRow
              key={skill.skill_id}
              skill={skill}
              onLaunch={onLaunch}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
